/**
 * MH-38 Nhật ký thao tác (đợt 3 phần C4, yêu cầu nghiệp vụ mục 5), dựng lại theo
 * bốn trang "Log" của bản mẫu CMS (`/quan-tri/nhat-ky/:nhom`) và giữ trang tra
 * cứu chung `/quan-tri/nhat-ky` (không nhóm) với đủ bộ lọc cũ.
 *
 * Phân nhóm bằng tham số THẬT của GET /api/v1/audit-log (`maThaoTac`,
 * `loaiDoiTuong` — so khớp bằng, một giá trị mỗi lần), theo các mã backend đang
 * ghi vào `nguoi_dung_nhat_ky`:
 *   - dang-nhap  : maThaoTac = 'dang_nhap' (hàm iam_ghi_nhat_ky_dang_nhap) — đủ.
 *   - trung-uong : maThaoTac = 'dat_cong_tac_dong_bo_tinh' — chỉ một phần.
 *   - cau-hinh   : loaiDoiTuong ∈ {nguoi_dung, nhom_nguoi_dung, don_vi_hanh_chinh,
 *                  tenant, adapter_service}, chọn một nhóm mỗi lần — một phần.
 *   - dieu-khien : backend CHƯA ghi lệnh điều khiển thiết bị vào nhật ký → bảng
 *                  rỗng theo mẫu + dải "Chưa có API", không hiện dữ liệu khác nhóm.
 *
 * CHỈ ĐỌC: không có nút sửa/xoá nào trên màn hình này, kể cả cho quản trị nền
 * tảng — máy chủ cũng không khai báo điểm cuối tương ứng (xem
 * `apps/api/src/nhat-ky-thao-tac/nhat-ky.controller.ts`).
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { vi } from '../ngon-ngu/vi';
import { viQuanTri } from '../ngon-ngu/vi-quan-tri';
import { nhatKyApi, type BoLocNhatKy, type DongNhatKy } from '../api/nhat-ky-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { BangChuaCoDuLieu, DaiChuaCoApi, TheTag, TruongLoc } from '../thanh-phan/MauGiaoDien';
import './quan-tri.css';

const t = viQuanTri.nhatKy;

type NhomNhatKy = 'dieu-khien' | 'dang-nhap' | 'trung-uong' | 'cau-hinh';

const LOAI_DOI_TUONG_CAU_HINH = [
  'nguoi_dung',
  'nhom_nguoi_dung',
  'don_vi_hanh_chinh',
  'tenant',
  'adapter_service',
] as const;

const CAU_HINH_NHOM: Record<NhomNhatKy, { tieuDe: string; canApi: readonly string[] }> = {
  'dieu-khien': {
    tieuDe: vi.menuCon.logDieuKhien,
    canApi: [
      'Ghi nhật ký thao tác cho POST /api/v1/devices/commands và POST /api/v1/devices/{id}/commands',
      'GET /api/v1/audit-log?nhomThaoTac=dieu_khien_thiet_bi',
    ],
  },
  'dang-nhap': { tieuDe: vi.menuCon.logDangNhap, canApi: [] },
  'trung-uong': {
    tieuDe: vi.menuCon.logTrungUong,
    canApi: [
      'GET /api/v1/audit-log?nhomThaoTac=gui_trung_uong (ghi nhật ký mỗi lần gửi gói tin lên trung ương)',
    ],
  },
  'cau-hinh': {
    tieuDe: vi.menuCon.logCauHinh,
    canApi: ['GET /api/v1/audit-log?nhomThaoTac=cau_hinh (hoặc maThaoTac/loaiDoiTuong nhận nhiều giá trị)'],
  },
};

const laNhom = (s: string | undefined): s is NhomNhatKy => s !== undefined && s in CAU_HINH_NHOM;

/** Ngày dạng yyyy-mm-dd (giờ địa phương) → epoch giây đầu/cuối ngày. */
function epochNgay(ngay: string, cuoiNgay: boolean): number | undefined {
  if (!ngay) return undefined;
  const d = new Date(`${ngay}T${cuoiNgay ? '23:59:59' : '00:00:00'}`);
  return Number.isNaN(d.getTime()) ? undefined : Math.floor(d.getTime() / 1000);
}

interface NhapLoc {
  tuNgay: string;
  denNgay: string;
  taiKhoan: string;
  maThaoTac: string;
  loaiDoiTuong: string;
  maDoiTuong: string;
  diaBan: string;
}

const LOC_TRONG: NhapLoc = {
  tuNgay: '',
  denNgay: '',
  taiKhoan: '',
  maThaoTac: '',
  loaiDoiTuong: '',
  maDoiTuong: '',
  diaBan: '',
};

export function MH38NhatKyThaoTac(): ReactNode {
  const { nhom: nhomThamSo } = useParams();
  const nhom = laNhom(nhomThamSo) ? nhomThamSo : null;
  const cauHinh = nhom ? CAU_HINH_NHOM[nhom] : null;

  // Đổi nhóm trên menu thì dựng lại toàn bộ trạng thái lọc (khoá theo nhóm).
  return (
    <NhatKyTheoNhom key={nhom ?? 'chung'} nhom={nhom} tieuDe={cauHinh?.tieuDe ?? vi.nhatKyThaoTac.tieuDe} />
  );
}

function NhatKyTheoNhom({ nhom, tieuDe }: { nhom: NhomNhatKy | null; tieuDe: string }): ReactNode {
  const [nhap, datNhap] = useState<NhapLoc>(LOC_TRONG);
  const [daApDung, datDaApDung] = useState<NhapLoc>(LOC_TRONG);
  const [loaiCauHinh, datLoaiCauHinh] = useState<string>(LOAI_DOI_TUONG_CAU_HINH[0]);
  const [trang, datTrang] = useState(1);
  const [dangXem, datDangXem] = useState<DongNhatKy | null>(null);

  const chuaCoDuLieu = nhom === 'dieu-khien';

  // Tham số cố định theo nhóm — ghi đè ô lọc tự do.
  const locNhom: BoLocNhatKy =
    nhom === 'dang-nhap'
      ? { maThaoTac: 'dang_nhap' }
      : nhom === 'trung-uong'
        ? { maThaoTac: 'dat_cong_tac_dong_bo_tinh' }
        : nhom === 'cau-hinh'
          ? { loaiDoiTuong: loaiCauHinh }
          : {};

  const boLoc: BoLocNhatKy = {
    nguoiThucHien: daApDung.taiKhoan || undefined,
    tuThoiDiem: epochNgay(daApDung.tuNgay, false),
    denThoiDiem: epochNgay(daApDung.denNgay, true),
    ...(nhom === null
      ? {
          maThaoTac: daApDung.maThaoTac || undefined,
          loaiDoiTuong: daApDung.loaiDoiTuong || undefined,
          maDoiTuong: daApDung.maDoiTuong || undefined,
          diaBan: daApDung.diaBan || undefined,
        }
      : {}),
    ...locNhom,
  };
  const dangLoc = Object.values(daApDung).some((v) => v !== '');

  const kq = dungTaiDuLieu<{ items: DongNhatKy[]; total: number }>({
    tai: (signal) => nhatKyApi.traCuu({ ...boLoc, page: trang, pageSize: SO_DONG_MAC_DINH }, signal),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [JSON.stringify(boLoc), trang],
    tat: chuaCoDuLieu,
  });

  const timKiem = (e: FormEvent): void => {
    e.preventDefault();
    datDaApDung(nhap);
    datTrang(1);
  };

  const xoaLoc = (): void => {
    datNhap(LOC_TRONG);
    datDaApDung(LOC_TRONG);
    datTrang(1);
  };

  const doiNhap = (k: keyof NhapLoc, v: string): void => datNhap((b) => ({ ...b, [k]: v }));

  const dong = kq.duLieu?.items ?? [];
  const stt = new Map(dong.map((d, i) => [d.id, (trang - 1) * SO_DONG_MAC_DINH + i + 1]));

  const cot: CotBang<DongNhatKy>[] = [
    { khoa: 'stt', nhan: t.cot.stt, ve: (d) => stt.get(d.id) ?? '' },
    { khoa: 'thoiDiem', nhan: t.cot.thoiDiem, ve: (d) => new Date(d.thoiDiem).toLocaleString('vi-VN') },
    {
      khoa: 'tenDangNhap',
      nhan: t.cot.tacNhan,
      ve: (d) => (
        <>
          {d.tenDangNhap}
          {d.diaChiIp ? <span className="bang__dong-phu">{d.diaChiIp}</span> : null}
        </>
      ),
    },
    {
      khoa: 'maThaoTac',
      nhan: t.cot.hanhDong,
      ve: (d) => t.hanhDong[d.maThaoTac] ?? d.maThaoTac,
    },
    {
      khoa: 'ketQua',
      nhan: t.cot.ketQua,
      ve: (d) =>
        d.ketQua === 'thanh_cong' ? (
          <TheTag kieu="ok">{t.ketQua.thanh_cong}</TheTag>
        ) : d.ketQua === 'that_bai' ? (
          <TheTag kieu="do">{t.ketQua.that_bai}</TheTag>
        ) : (
          <TheTag>{d.ketQua}</TheTag>
        ),
    },
    {
      khoa: 'doiTuong',
      nhan: t.cot.doiTuong,
      ve: (d) => {
        const loai = d.loaiDoiTuong ? (t.loaiDoiTuong[d.loaiDoiTuong] ?? d.loaiDoiTuong) : '';
        const chu = `${loai}${d.maDoiTuong ? ` · ${d.maDoiTuong}` : ''}`;
        return chu || (d.moTa ?? '—');
      },
    },
    {
      khoa: 'chiTiet',
      nhan: '',
      ve: (d) => (
        <button type="button" className="nut nut--nho" onClick={() => datDangXem(d)}>
          {vi.nhatKyThaoTac.xemChiTiet}
        </button>
      ),
    },
  ];

  const canApi = nhom ? CAU_HINH_NHOM[nhom].canApi : [];

  return (
    <div className="man-hinh man-hinh--nhat-ky-thao-tac" data-nhom={nhom ?? 'chung'}>
      <header className="man-hinh__dau">
        <h1>{tieuDe}</h1>
      </header>
      <p className="the the--phu">{vi.nhatKyThaoTac.ghiChuChiDoc}</p>

      {canApi.length > 0 ? <DaiChuaCoApi canApi={canApi} /> : null}
      {nhom ? <p className="qt-ghi-chu">{t.ghiChuNhom[nhom]}</p> : null}

      <form className="qt-hang-loc man-hinh__bo-loc" onSubmit={timKiem}>
        <TruongLoc nhan={t.khoangNgay}>
          <span className="qt-khoang-ngay">
            <input
              type="date"
              value={nhap.tuNgay}
              onChange={(e) => doiNhap('tuNgay', e.target.value)}
              aria-label={t.tuNgay}
              disabled={chuaCoDuLieu}
            />
            <span aria-hidden="true">→</span>
            <input
              type="date"
              value={nhap.denNgay}
              onChange={(e) => doiNhap('denNgay', e.target.value)}
              aria-label={t.denNgay}
              disabled={chuaCoDuLieu}
            />
          </span>
        </TruongLoc>
        <TruongLoc nhan={t.taiKhoan} htmlFor="nk-tai-khoan">
          <input
            id="nk-tai-khoan"
            value={nhap.taiKhoan}
            placeholder={t.nhapTaiKhoan}
            onChange={(e) => doiNhap('taiKhoan', e.target.value)}
            disabled={chuaCoDuLieu}
          />
        </TruongLoc>
        {nhom === 'cau-hinh' ? (
          <TruongLoc nhan={t.loaiDoiTuongCauHinh} htmlFor="nk-loai-cau-hinh">
            <select
              id="nk-loai-cau-hinh"
              value={loaiCauHinh}
              onChange={(e) => {
                datLoaiCauHinh(e.target.value);
                datTrang(1);
              }}
            >
              {LOAI_DOI_TUONG_CAU_HINH.map((l) => (
                <option key={l} value={l}>
                  {t.loaiDoiTuong[l] ?? l}
                </option>
              ))}
            </select>
          </TruongLoc>
        ) : null}
        {nhom === null ? (
          <>
            <TruongLoc nhan={vi.nhatKyThaoTac.boLoc.maThaoTac} htmlFor="nk-ma-thao-tac">
              <input
                id="nk-ma-thao-tac"
                value={nhap.maThaoTac}
                onChange={(e) => doiNhap('maThaoTac', e.target.value)}
              />
            </TruongLoc>
            <TruongLoc nhan={vi.nhatKyThaoTac.boLoc.loaiDoiTuong} htmlFor="nk-loai-doi-tuong">
              <input
                id="nk-loai-doi-tuong"
                value={nhap.loaiDoiTuong}
                onChange={(e) => doiNhap('loaiDoiTuong', e.target.value)}
              />
            </TruongLoc>
            <TruongLoc nhan={vi.nhatKyThaoTac.boLoc.maDoiTuong} htmlFor="nk-ma-doi-tuong">
              <input
                id="nk-ma-doi-tuong"
                value={nhap.maDoiTuong}
                onChange={(e) => doiNhap('maDoiTuong', e.target.value)}
              />
            </TruongLoc>
            <TruongLoc nhan={vi.nhatKyThaoTac.boLoc.diaBan} htmlFor="nk-dia-ban">
              <input
                id="nk-dia-ban"
                value={nhap.diaBan}
                onChange={(e) => doiNhap('diaBan', e.target.value)}
              />
            </TruongLoc>
          </>
        ) : null}
        <button type="submit" className="nut nut--chinh" disabled={chuaCoDuLieu}>
          {t.timKiem}
        </button>
      </form>

      {dangXem ? (
        <div className="hop-thoai" data-vung="chi-tiet-nhat-ky" role="dialog">
          <h2>{vi.nhatKyThaoTac.xemChiTiet}</h2>
          <p>
            {vi.nhatKyThaoTac.cot.maThaoTac}: <strong>{dangXem.maThaoTac}</strong>
          </p>
          <div className="man-hinh__bo-cuc-hai-cot">
            <div>
              <h3>{vi.nhatKyThaoTac.giaTriTruoc}</h3>
              <pre>{JSON.stringify(dangXem.giaTriTruoc, null, 2)}</pre>
            </div>
            <div>
              <h3>{vi.nhatKyThaoTac.giaTriSau}</h3>
              <pre>{JSON.stringify(dangXem.giaTriSau, null, 2)}</pre>
            </div>
          </div>
          <button type="button" className="nut" onClick={() => datDangXem(null)}>
            {vi.nhatKyThaoTac.dong}
          </button>
        </div>
      ) : null}

      {chuaCoDuLieu ? (
        <BangChuaCoDuLieu
          nhan={tieuDe}
          cot={[t.cot.stt, t.cot.thoiDiem, t.cot.tacNhan, t.cot.hanhDong, t.cot.ketQua, t.cot.doiTuong]}
        />
      ) : (
        <BangDuLieu<DongNhatKy>
          nhan={tieuDe}
          cot={cot}
          dong={dong}
          khoaDong={(d) => d.id}
          trangThai={kq.trangThai}
          loi={kq.loi}
          taiLau={kq.taiLau}
          onThuLai={kq.taiLai}
          tong={kq.duLieu?.total ?? 0}
          trang={trang}
          soDongMoiTrang={SO_DONG_MAC_DINH}
          onDoiTrang={datTrang}
          onDoiSoDong={() => undefined}
          dangLoc={dangLoc}
          onXoaLoc={xoaLoc}
          moTaRong={vi.nhatKyThaoTac.rongMoTa}
        />
      )}
    </div>
  );
}
